import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getDashboardStats, getRekapSekolah, getKinerjaAggregate, getSekolahList, getUsersFaceStatus, getLaporanKinerjaInstansi, getKinerjaAggregateInstansi } from '../../lib/api';
import { getMonthName } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Building2, Users, UserCheck, UserX, TrendingUp,
  MapPin, FileText, Settings, RefreshCw, Loader2,
  BarChart3, Award, UsersRound, ChevronRight, Trophy,
  ShieldAlert, AlertCircle, Landmark, Briefcase, Clock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const DISPLAY_LIMIT = 10; // Top N sekolah untuk ditampilkan

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [rekapSekolah, setRekapSekolah] = useState([]);
  const [totalSekolah, setTotalSekolah] = useState(0);
  const [kinerjaAggregate, setKinerjaAggregate] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [rekapLoading, setRekapLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [faceStatus, setFaceStatus] = useState(null);
  const [faceStatusLoading, setFaceStatusLoading] = useState(true);
  // Instansi state
  const [instansiKinerja, setInstansiKinerja] = useState([]);
  const [instansiAggregate, setInstansiAggregate] = useState(null);
  const [instansiLoading, setInstansiLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [selectedMonth, selectedYear]);

  const loadData = async () => {
    setStatsLoading(true);
    setRekapLoading(true);
    setInstansiLoading(true);

    // Load stats first (fast)
    getDashboardStats()
      .then((statsData) => {
        if (statsData) setStats(statsData);
        setStatsLoading(false);
      })
      .catch(() => setStatsLoading(false));

    // Load rekap sekolah with limit (Top N only for display)
    getRekapSekolah(selectedMonth, selectedYear, { limit: DISPLAY_LIMIT })
      .then((rekap) => {
        setRekapSekolah(rekap?.data || []);
        setTotalSekolah(rekap?.total || 0);
      })
      .catch(() => setRekapSekolah([]))
      .finally(() => setRekapLoading(false));

    // Load kinerja aggregate for pie chart
    getKinerjaAggregate(selectedMonth, selectedYear)
      .then((aggregate) => {
        setKinerjaAggregate(aggregate);
      })
      .catch(() => setKinerjaAggregate(null));

    // Load face registration status
    getUsersFaceStatus()
      .then((data) => {
        setFaceStatus(data);
        setFaceStatusLoading(false);
      })
      .catch(() => setFaceStatusLoading(false));

    // Load instansi performance data
    getLaporanKinerjaInstansi(selectedMonth, selectedYear, null, DISPLAY_LIMIT)
      .then((data) => {
        setInstansiKinerja(data?.data || []);
      })
      .catch(() => setInstansiKinerja([]))
      .finally(() => setInstansiLoading(false));

    getKinerjaAggregateInstansi(selectedMonth, selectedYear)
      .then((data) => {
        setInstansiAggregate(data);
      })
      .catch(() => setInstansiAggregate(null));
  };

  // Chart data - Top N sekolah only
  const chartData = rekapSekolah.slice(0, DISPLAY_LIMIT).map(s => ({
    name: s.sekolah_nama.length > 15 ? s.sekolah_nama.substring(0, 15) + '...' : s.sekolah_nama,
    skor: s.skor_rata_rata,
    guru: s.total_guru
  }));

  // Pie chart data for today's attendance
  const pieDataToday = [
    { name: 'Tepat Waktu', value: stats?.hadir_tepat_waktu || 0, color: '#10b981' },
    { name: 'Terlambat', value: stats?.hadir_terlambat || 0, color: '#f59e0b' },
    { name: 'Mangkir', value: stats?.mangkir || 0, color: '#ef4444' },
    { name: 'Belum Absen', value: stats?.belum_absen || 0, color: '#94a3b8' }
  ].filter(d => d.value > 0);

  // Pie chart for teacher grade distribution
  const pieDataGrades = kinerjaAggregate?.distribusi?.map(d => ({
    name: `${d.grade} - ${d.label}`,
    value: d.count,
    color: d.color
  })).filter(d => d.value > 0) || [];

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-indigo-950">Dashboard Admin Dinas</h1>
          <p className="text-gray-500">{user?.nama}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
            <SelectTrigger className="w-[140px]" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 12 }, (_, i) => (
                <SelectItem key={i + 1} value={String(i + 1)}>
                  {getMonthName(i + 1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-[100px]" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadData} className="border-indigo-200">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <Card className="border-indigo-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-950">{stats?.total_sekolah || 0}</p>
                <p className="text-xs text-gray-500">Total Sekolah</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-indigo-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-950">{stats?.total_guru || 0}</p>
                <p className="text-xs text-gray-500">Total Guru/Staff</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Landmark className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats?.total_instansi || 0}</p>
                <p className="text-xs text-gray-500">Total Instansi</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stats?.total_pegawai_instansi || 0}</p>
                <p className="text-xs text-gray-500">Pegawai Instansi</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-indigo-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-950">{stats?.sudah_absen || 0}</p>
                <p className="text-xs text-gray-500">Sudah Absen</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-indigo-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <UserX className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-950">{stats?.belum_absen || 0}</p>
                <p className="text-xs text-gray-500">Belum Absen</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Today's Attendance Summary - Sekolah vs Instansi */}
      {stats && (stats.total_instansi > 0 || stats.total_pegawai_instansi > 0) && (
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Card className="border-indigo-100/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-semibold text-indigo-950">Kehadiran Sekolah Hari Ini</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <UserCheck className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-emerald-700 font-medium">{stats?.sekolah_sudah_absen || 0} hadir</span>
                </div>
                <div className="flex items-center gap-1">
                  <UserX className="w-3.5 h-3.5 text-red-400" />
                  <span className="text-red-600">{stats?.sekolah_belum_absen || 0} belum</span>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Landmark className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-slate-800">Kehadiran Instansi Hari Ini</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <UserCheck className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-emerald-700 font-medium">{stats?.instansi_sudah_absen || 0} hadir</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-amber-700">{stats?.instansi_terlambat || 0} terlambat</span>
                </div>
                <div className="flex items-center gap-1">
                  <UserX className="w-3.5 h-3.5 text-red-400" />
                  <span className="text-red-600">{stats?.instansi_belum_absen || 0} belum</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Attendance Pie Chart - Today */}
        <Card className="border-indigo-100/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Distribusi Kehadiran Hari Ini
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {pieDataToday.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieDataToday}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {pieDataToday.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  Tidak ada data kehadiran
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Teacher Grade Distribution Pie Chart */}
        <Card className="border-indigo-100/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              Distribusi Kinerja Guru
            </CardTitle>
            <CardDescription>
              {kinerjaAggregate?.total_guru || 0} guru • Rata-rata: {kinerjaAggregate?.skor_rata_rata?.toFixed(1) || 0}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {pieDataGrades.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieDataGrades}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {pieDataGrades.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  Tidak ada data kinerja
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* School Performance Bar Chart - Top N */}
      <Card className="border-indigo-100/50 mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Top {DISPLAY_LIMIT} Skor Kinerja Sekolah
            </CardTitle>
            <CardDescription>
              {getMonthName(selectedMonth)} {selectedYear} • Menampilkan {Math.min(DISPLAY_LIMIT, totalSekolah)} dari {totalSekolah} sekolah
            </CardDescription>
          </div>
          {totalSekolah > DISPLAY_LIMIT && (
            <Button variant="outline" size="sm" onClick={() => navigate('/laporan')}>
              Lihat Semua
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="h-72">
            {rekapLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
              </div>
            ) : chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" domain={[0, 100]} />
                  <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip 
                    formatter={(value) => [`${value.toFixed(1)}`, 'Skor']}
                    labelFormatter={(label) => `Sekolah: ${label}`}
                  />
                  <Bar dataKey="skor" fill="#4f46e5" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                Tidak ada data sekolah
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* School Ranking Table - Top N */}
      <Card className="border-indigo-100/50 mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5" />
              Peringkat Top {DISPLAY_LIMIT} Sekolah
            </CardTitle>
            <CardDescription>
              {getMonthName(selectedMonth)} {selectedYear}
            </CardDescription>
          </div>
          {totalSekolah > DISPLAY_LIMIT && (
            <Button variant="outline" size="sm" onClick={() => navigate('/laporan')}>
              Lihat Semua {totalSekolah} Sekolah
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            {rekapLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
              </div>
            ) : rekapSekolah.length > 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Peringkat</th>
                  <th>Sekolah</th>
                  <th>Total Guru</th>
                  <th>Tepat Waktu</th>
                  <th>Terlambat</th>
                  <th>Mangkir</th>
                  <th>Skor</th>
                </tr>
              </thead>
              <tbody>
                {rekapSekolah.map((sekolah, idx) => (
                  <tr key={sekolah.sekolah_id}>
                    <td>
                      <div className="flex items-center gap-2">
                        {idx < 3 ? (
                          <Badge className={
                            idx === 0 ? 'bg-amber-400 text-amber-900' :
                            idx === 1 ? 'bg-gray-300 text-gray-700' :
                            'bg-amber-600 text-white'
                          }>
                            #{sekolah.ranking || idx + 1}
                          </Badge>
                        ) : (
                          <span className="text-gray-500 pl-2">{sekolah.ranking || idx + 1}</span>
                        )}
                      </div>
                    </td>
                    <td className="font-medium">{sekolah.sekolah_nama}</td>
                    <td>{sekolah.total_guru}</td>
                    <td className="text-emerald-600">{sekolah.hadir_tepat_waktu}</td>
                    <td className="text-amber-600">{sekolah.hadir_terlambat}</td>
                    <td className="text-red-600">{sekolah.mangkir}</td>
                    <td>
                      <Badge className={
                        sekolah.skor_rata_rata >= 90 ? 'bg-emerald-100 text-emerald-800' :
                        sekolah.skor_rata_rata >= 75 ? 'bg-blue-100 text-blue-800' :
                        sekolah.skor_rata_rata >= 60 ? 'bg-amber-100 text-amber-800' :
                        'bg-red-100 text-red-800'
                      }>
                        {sekolah.skor_rata_rata.toFixed(1)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Tidak ada data sekolah
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pegawai Instansi Performance Section */}
      {(instansiKinerja.length > 0 || instansiAggregate) && (
        <Card className="border-slate-200 mb-6" data-testid="instansi-performance-section">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-slate-800">
                <Landmark className="w-5 h-5 text-blue-600" />
                Kinerja Pegawai Instansi
              </CardTitle>
              <CardDescription>
                {getMonthName(selectedMonth)} {selectedYear} • {instansiAggregate?.total_pegawai || 0} pegawai
                {instansiAggregate?.avg_skor_akhir ? ` • Rata-rata skor: ${instansiAggregate.avg_skor_akhir.toFixed(1)}` : ''}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/laporan-instansi')}>
              Lihat Detail
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {/* Instansi Aggregate Stats */}
            {instansiAggregate && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <p className="text-xs text-blue-600 font-medium">Total Pegawai</p>
                  <p className="text-xl font-bold text-blue-800">{instansiAggregate.total_pegawai || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                  <p className="text-xs text-emerald-600 font-medium">Rata-rata Kehadiran</p>
                  <p className="text-xl font-bold text-emerald-800">{(instansiAggregate.avg_kehadiran || 0).toFixed(1)}%</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                  <p className="text-xs text-amber-600 font-medium">Rata-rata Ketepatan</p>
                  <p className="text-xl font-bold text-amber-800">{(instansiAggregate.avg_ketepatan || 0).toFixed(1)}%</p>
                </div>
                <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100">
                  <p className="text-xs text-indigo-600 font-medium">Skor Akhir</p>
                  <p className="text-xl font-bold text-indigo-800">{(instansiAggregate.avg_skor_akhir || 0).toFixed(1)}</p>
                </div>
              </div>
            )}

            {/* Instansi Pegawai Table */}
            <div className="overflow-x-auto">
              {instansiLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
                </div>
              ) : instansiKinerja.length > 0 ? (
                <table className="data-table" data-testid="instansi-kinerja-table">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Nama</th>
                      <th>Jabatan</th>
                      <th>Instansi</th>
                      <th>Tepat Waktu</th>
                      <th>Terlambat</th>
                      <th>Mangkir</th>
                      <th>Skor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {instansiKinerja.slice(0, DISPLAY_LIMIT).map((pegawai, idx) => (
                      <tr key={pegawai.pegawai_id || idx}>
                        <td>{idx + 1}</td>
                        <td className="font-medium">{pegawai.pegawai_nama}</td>
                        <td className="text-sm text-gray-600">{pegawai.jabatan || '-'}</td>
                        <td className="text-sm">{pegawai.instansi_nama || '-'}</td>
                        <td className="text-emerald-600">{pegawai.hadir_tepat_waktu || 0}</td>
                        <td className="text-amber-600">{pegawai.hadir_terlambat || 0}</td>
                        <td className="text-red-600">{pegawai.total_mangkir || 0}</td>
                        <td>
                          <Badge className={
                            (pegawai.skor_akhir || 0) >= 90 ? 'bg-emerald-100 text-emerald-800' :
                            (pegawai.skor_akhir || 0) >= 75 ? 'bg-blue-100 text-blue-800' :
                            (pegawai.skor_akhir || 0) >= 60 ? 'bg-amber-100 text-amber-800' :
                            'bg-red-100 text-red-800'
                          }>
                            {(pegawai.skor_akhir || 0).toFixed(1)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Tidak ada data pegawai instansi untuk periode ini
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Face Registration Status Card */}
      {!faceStatusLoading && faceStatus && faceStatus.without_face > 0 && (
        <Card className="border-red-200 bg-red-50/30 mb-6" data-testid="face-status-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-red-800">
                <ShieldAlert className="w-5 h-5 text-red-600" />
                Pegawai Belum Daftar Wajah ({faceStatus.without_face})
              </CardTitle>
              <Badge className="bg-red-100 text-red-700 px-3">
                {faceStatus.without_face} / {faceStatus.total}
              </Badge>
            </div>
            <CardDescription className="text-red-600">
              Pegawai berikut belum mendaftarkan wajah. Setelah grace period 2 hari, mereka tidak dapat melakukan absensi.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nama</th>
                    <th>NIP</th>
                    <th>Role</th>
                    <th>Organisasi</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {faceStatus.users.filter(u => !u.has_face).slice(0, 20).map((u) => {
                    const createdAt = u.created_at ? new Date(u.created_at) : null;
                    const daysSinceCreated = createdAt ? Math.floor((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24)) : null;
                    const graceExpired = daysSinceCreated !== null && daysSinceCreated > 2;
                    return (
                      <tr key={u.id}>
                        <td className="font-medium">{u.nama}</td>
                        <td className="font-mono text-sm">{u.nip || '-'}</td>
                        <td>
                          <Badge className="bg-gray-100 text-gray-700 text-xs">
                            {u.role === 'pegawai_instansi' ? 'Pegawai Instansi' : 
                             u.role === 'guru' ? 'Guru' : 
                             u.role === 'kepala_sekolah' ? 'Kepala Sekolah' :
                             u.role === 'tenaga_tata_usaha' ? 'TU' : u.role}
                          </Badge>
                        </td>
                        <td className="text-sm">{u.organisasi_nama}</td>
                        <td>
                          {graceExpired ? (
                            <Badge className="bg-red-100 text-red-700">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Terblokir
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">
                              Grace {daysSinceCreated !== null ? `${2 - daysSinceCreated} hari` : '-'}
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {faceStatus.without_face > 20 && (
                <p className="text-sm text-red-600 mt-2 text-center">
                  Menampilkan 20 dari {faceStatus.without_face} pegawai. Lihat selengkapnya di halaman Manajemen User.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Button
          variant="outline"
          className="h-12 border-indigo-200"
          onClick={() => navigate('/sekolah')}
          data-testid="kelola-sekolah-btn"
        >
          <Building2 className="w-4 h-4 mr-2" />
          Kelola Sekolah
        </Button>
        <Button
          variant="outline"
          className="h-12 border-slate-200"
          onClick={() => navigate('/instansi')}
          data-testid="kelola-instansi-btn"
        >
          <Landmark className="w-4 h-4 mr-2" />
          Kelola Instansi
        </Button>
        <Button
          variant="outline"
          className="h-12 border-indigo-200"
          onClick={() => navigate('/users')}
          data-testid="kelola-users-btn"
        >
          <UsersRound className="w-4 h-4 mr-2" />
          Manajemen User
        </Button>
        <Button
          variant="outline"
          className="h-12 border-indigo-200"
          onClick={() => navigate('/laporan')}
          data-testid="laporan-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          Laporan Kinerja
        </Button>
        <Button
          variant="outline"
          className="h-12 border-indigo-200"
          onClick={() => navigate('/peta')}
          data-testid="peta-btn"
        >
          <MapPin className="w-4 h-4 mr-2" />
          Peta Absensi
        </Button>
        <Button
          variant="outline"
          className="h-12 border-indigo-200"
          onClick={() => navigate('/audit')}
          data-testid="audit-btn"
        >
          <Settings className="w-4 h-4 mr-2" />
          Audit Log
        </Button>
      </div>
    </div>
  );
};

export default AdminDashboard;
